/**
 * Format comparison — grouped horizontal-bar comparison primitive.
 *
 * One card per format, showing reached / rsvp / attended bars side-by-side
 * (stacked rows, all scaled against the global `reached` max so the visual
 * ordering matches the prototype's A3 / FormatEffectivenessMini panel).
 *
 * The hosting page composes one or more `<FormatComparison/>` instances —
 * the `.format-grid` + `.format-card` CSS already lives in dashboard.css.
 */
import { formatNumber } from '../../utils/format';

export interface FormatComparisonRow {
  id: string;
  /** Hebrew display name. */
  label: string;
  events: number;
  reached: number;
  rsvp_yes: number;
  attended: number;
  walk_in: number;
  /** Derived (attended - walk_in) / rsvp_yes. */
  yes_show: number;
}

export interface FormatComparisonProps {
  rows: FormatComparisonRow[];
}

/**
 * Heuristic for "this format doesn't record attendance" — when there
 * are real RSVPs (>10) but zero attended check-ins, the format almost
 * certainly lacks a check-in step (חוג בית, אירוע ווירטואלי, …) rather
 * than nobody having shown up.
 */
const NO_ATTENDANCE_RECORDING_RSVP_FLOOR = 10;

function lacksAttendanceRecording(r: FormatComparisonRow): boolean {
  return r.attended === 0 && r.rsvp_yes > NO_ATTENDANCE_RECORDING_RSVP_FLOOR;
}

export function FormatComparison({ rows }: FormatComparisonProps) {
  // Shared scale across cards so the bars are visually comparable.
  const reachedMax = Math.max(1, ...rows.map((r) => r.reached));

  return (
    <div className="format-grid">
      {rows.map((r) => {
        const avg = r.events > 0 ? Math.round(r.attended / r.events) : 0;
        const walkInShare = r.attended > 0 ? r.walk_in / r.attended : 0;
        const noAttendanceRec = lacksAttendanceRecording(r);
        return (
          <div key={r.id} className="format-card">
            <div className="fc-name">{r.label}</div>
            <div className="fc-meta">
              {formatNumber(r.events)} אירועים · ממוצע {formatNumber(avg)} הגעות לאירוע
            </div>
            <div className="stages">
              <StageRow
                label="נחשפו"
                value={r.reached}
                widthPct={(r.reached / reachedMax) * 100}
              />
              <StageRow
                label="אישרו הגעה"
                value={r.rsvp_yes}
                widthPct={(r.rsvp_yes / reachedMax) * 100}
              />
              <StageRow
                label="הגיעו"
                value={r.attended}
                widthPct={(r.attended / reachedMax) * 100}
                accent
              />
            </div>
            <div className="conv">
              <div className="big">{Math.round(r.yes_show * 100)}%</div>
              <div className="lbl">
                המרת אישור←הגעה{r.walk_in > 0 ? ` · ${Math.round(walkInShare * 100)}% walk-in` : ''}
              </div>
            </div>
            {noAttendanceRec && (
              <div
                className="fc-coverage-note"
                style={{
                  marginTop: 10,
                  fontSize: 11,
                  color: 'var(--ink-3)',
                  background: 'var(--paper-2, #faf8f3)',
                  borderRadius: 4,
                  padding: '4px 8px',
                  lineHeight: 1.4,
                }}
              >
                ℹ️ נוכחות לא נרשמת בפורמט זה (אין רישום כניסה)
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

interface StageRowProps {
  label: string;
  value: number;
  widthPct: number;
  accent?: boolean;
}

function StageRow({ label, value, widthPct, accent }: StageRowProps) {
  const w = Number.isFinite(widthPct) ? Math.max(0, Math.min(100, widthPct)) : 0;
  return (
    <div className="stage-row">
      <span className="lbl">{label}</span>
      <span className="bar">
        <span
          style={{
            width: `${w}%`,
            ...(accent ? { background: 'var(--accent)' } : null),
          }}
        />
      </span>
      <span className="v mono">{formatNumber(value)}</span>
    </div>
  );
}
